import { z } from 'zod'

/**
 * HTTP core for the Bloo API.
 *
 * Discovery facts this design leans on (docs/inventory/FINDINGS.md):
 * - One origin, six path prefixes (/api /lc /mgmt /wrk /info /unet) — so one
 *   client with relative paths; dev proxying picks the origin.
 * - RPC over POST: reads are POSTs, so nothing retries by default.
 * - Auth is an SSID header on everything past login.
 * - Error bodies look like { status: 'error', message: string }.
 */

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown
  /**
   * The message the server actually sent, or undefined when `message` is our
   * own fallback phrasing. Call sites that want to add domain-specific wording
   * (e.g. "Invalid email or password" on login) must only do so when the
   * server said nothing -- never overwrite a real server message.
   */
  readonly serverMessage: string | undefined
  constructor(status: number, message: string, body: unknown, serverMessage?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.serverMessage = serverMessage
  }
}

export interface ClientOptions {
  /** Origin prefix; '' in production (same origin) and under the dev proxy. */
  baseUrl?: string
  /** Returns the current SSID, or null before login. */
  getSsid?: () => string | null
  /** Called on a 401/419 so the app can drop the session. */
  onUnauthorized?: () => void
  /**
   * Abort a request that has not produced a response body within this many ms.
   * Defaults to 30s. Pass 0 to disable.
   *
   * Without this a hung connection leaves the promise pending forever: the UI
   * spins with no way out, and any caller holding interim state holds it
   * indefinitely (see establishLive / setTransientSession in the console).
   */
  timeoutMs?: number
}

export interface ApiClient {
  request: <T>(
    method: HttpMethod,
    path: string,
    // ZodType<T, ZodTypeDef, unknown>: T is the OUTPUT type, so schemas using
    // .default() report the post-parse shape rather than the input shape.
    opts?: {
      /**
       * JSON-encoded, unless it is a FormData — that is sent as-is, with the
       * Content-Type header removed so the browser can set the multipart
       * boundary. Only plugin save_config needs that.
       */
      body?: unknown
      schema?: z.ZodType<T, z.ZodTypeDef, unknown>
      auth?: boolean
      /** Skip the envelope-status check below. See checkEnvelopeStatus. */
      rawEnvelope?: boolean
      /** Extra request headers, merged over the defaults. */
      headers?: Record<string, string>
      /**
       * `blob`: a successful answer is returned as its bytes (a Blob), unparsed —
       * evidence downloads. Errors still read as JSON `detail`s.
       */
      responseType?: 'json' | 'blob'
      /**
       * A file upload (a FormData body): sent over XMLHttpRequest so the
       * timeout can watch the upload itself — aborted only when no bytes
       * have gone out for `timeoutMs`, never because the whole body takes
       * longer (a large file on a slow link is not a hung server). Once the
       * body is sent, the two phases below apply as to any request.
       */
      upload?: boolean
    },
  ) => Promise<T>
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

const errorBody = z
  .object({
    message: z.string().optional(),
    error: z.string().optional(),
    // FastAPI services: a string, a validation list, or `{code, message, …}`.
    detail: z.unknown().optional(),
    status: z.string().optional(),
  })
  .passthrough()

/** FastAPI's `detail` as one sentence — `body.title: String should have …` for a validation list. */
function detailMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const parts = detail.map((e) => {
      const err = (e ?? {}) as { loc?: unknown[]; msg?: unknown; message?: unknown; field?: unknown }
      const msg = typeof err.msg === 'string' ? err.msg : typeof err.message === 'string' ? err.message : 'invalid'
      const loc = Array.isArray(err.loc) ? err.loc.map(String).join('.') : typeof err.field === 'string' ? err.field : ''
      return loc ? `${loc}: ${msg}` : msg
    })
    return parts.length ? parts.join('; ') : undefined
  }
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  return undefined
}

/**
 * Human phrasing when the server sends a status code but no usable message.
 *
 * These stay DOMAIN-NEUTRAL on purpose. A previous version mapped 400/401 to
 * "Invalid email or password." here, in the shared HTTP core -- so any 400 from
 * any endpoint, including ones that only run AFTER authentication succeeds,
 * was reported to the user as a bad password. Credential phrasing belongs at
 * the login call site (see auth.ts), which is the only place that knows the
 * request was a credential check.
 */
function fallbackMessage(status: number): string {
  switch (status) {
    case 400:
      return 'The server rejected that request.'
    case 401:
      return 'Your session is not valid. Sign in again.'
    case 403:
      return 'You do not have access to that.'
    case 404:
      return 'Not found.'
    case 429:
      return 'Too many attempts. Try again shortly.'
    default:
      return status >= 500 ? 'The server had a problem. Try again.' : `Request failed (${status}).`
  }
}

/**
 * The legacy backend reports a dead session INSIDE a 200 response body.
 *
 * VERIFIED 2026-08-21 against ap1 (docs/inventory/search-capture.md). A request
 * carrying a bogus SSID came back:
 *
 *     HTTP 200   { "status": "invalid", "status_code": 400 }
 *
 * -- not a 401. So checking `res.ok` alone, which is all this client did until
 * now, means an expired session renders an empty page on every console screen
 * instead of landing on login. The legacy console has always read the body
 * instead (saas-console-docs/dataflow/pages/_patterns.md, Pattern 3); it just
 * never checked the HTTP status, so the two halves of the contract were each
 * only half-implemented.
 *
 * Deliberately NARROW. Only the two envelope states that mean "this session or
 * this user can no longer do this" are handled here, because only those need to
 * reach the app as an interrupt rather than as data:
 *
 *   status: 'invalid'                              -- session gone
 *   status: 'failed' + 'user not authorised'       -- ACL changed mid-session
 *
 * Everything else -- including `status: 'error'`, which is how /wrk/api/job/invoke
 * reports a DQL parse failure -- is domain data and belongs to the module that
 * knows what it means (see search.ts). A blanket "any non-success throws" here
 * would turn every recoverable domain error into an HTTP-level exception.
 *
 * Pass `rawEnvelope: true` to skip this for an endpoint whose payload happens to
 * carry a `status` field of its own meaning.
 */
function checkEnvelopeStatus(json: unknown, onUnauthorized?: () => void): void {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return
  const env = json as { status?: unknown; message?: unknown }
  if (typeof env.status !== 'string') return
  const message = typeof env.message === 'string' ? env.message : undefined

  if (env.status === 'invalid') {
    onUnauthorized?.()
    throw new ApiError(401, message ?? fallbackMessage(401), json, message)
  }
  if (env.status === 'failed' && message === 'user not authorised') {
    throw new ApiError(403, fallbackMessage(403), json, message)
  }
}

const noResponse = () => new ApiError(0, 'The server did not respond. Check your connection and try again.', null)

/**
 * An upload over XMLHttpRequest — fetch cannot see a request body's
 * progress. Three phases, each `timeoutMs` (0 = no limit): while the body
 * goes out, the timer restarts on every progress event, so only a stall
 * aborts it; once it is sent, the answer has `timeoutMs` to start (the
 * service scans the file first), then `timeoutMs * 4` for its body, as
 * fetch requests do. An answer that starts while the body is still going out
 * (an early 413 or 401) ends the sending phase: later upload events no
 * longer touch the timer. A network failure rejects as fetch does (TypeError).
 */
function sendUpload(
  url: string,
  method: HttpMethod,
  headers: Record<string, string>,
  body: FormData,
  timeoutMs: number,
): Promise<{ ok: boolean; status: number; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let timer: ReturnType<typeof setTimeout> | undefined
    /** Headers are in: the body has its longer allowance, and upload events no longer touch the timer. */
    let answered = false
    const arm = (ms: number) => {
      clearTimeout(timer)
      if (timeoutMs > 0) timer = setTimeout(() => xhr.abort(), ms)
    }
    /** While sending: any bytes out restart the stall timer — until the server has answered (it may answer early, e.g. a 413). */
    const sending = () => {
      if (!answered) arm(timeoutMs)
    }
    xhr.open(method, url)
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = sending
    xhr.upload.onload = sending
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 2 && !answered) {
        answered = true
        arm(timeoutMs * 4)
      }
    }
    xhr.onload = () => {
      clearTimeout(timer)
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, text: xhr.responseText })
    }
    xhr.onabort = () => {
      clearTimeout(timer)
      reject(noResponse())
    }
    xhr.onerror = () => {
      clearTimeout(timer)
      reject(new TypeError('Failed to fetch'))
    }
    arm(timeoutMs)
    xhr.send(body)
  })
}

export function createClient(options: ClientOptions = {}): ApiClient {
  const { baseUrl = '', getSsid, onUnauthorized, timeoutMs = 30_000 } = options

  return {
    async request<T>(
      method: HttpMethod,
      path: string,
      opts: {
        body?: unknown
        schema?: z.ZodType<T, z.ZodTypeDef, unknown>
        auth?: boolean
        rawEnvelope?: boolean
        headers?: Record<string, string>
        responseType?: 'json' | 'blob'
        upload?: boolean
      } = {},
    ): Promise<T> {
      const { body, schema, auth = true, rawEnvelope = false } = opts

      /**
       * One endpoint in this API takes a multipart body: plugin save_config,
       * when the configuration carries a key file
       * (docs/settings-requirements.md §4.6). FormData is passed through
       * untouched and the Content-Type header is DELETED, not replaced — the
       * browser has to set it itself so it can append the multipart boundary.
       * Setting 'multipart/form-data' by hand is as broken as leaving
       * 'application/json': the request arrives with no boundary and the
       * server cannot split the parts.
       */
      const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData

      const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
      if (isMultipart) delete headers['Content-Type']
      if (auth) {
        const ssid = getSsid?.()
        if (ssid) headers.SSID = ssid
      }
      Object.assign(headers, opts.headers)

      let res: { ok: boolean; status: number }
      let text: string
      if (opts.upload && isMultipart && typeof XMLHttpRequest !== 'undefined') {
        const up = await sendUpload(`${baseUrl}${path}`, method, headers, body as FormData, timeoutMs)
        res = up
        text = up.text
      } else {
        // Two phases. The timeout proper guards time-to-headers — a server that
        // has not answered at all. Once headers arrive the BODY gets its own,
        // much longer allowance: a stalled body is still a hang worth catching,
        // but a large result actively streaming over a slow link is not, and
        // aborting it mid-transfer misreports a live server as down.
        const controller = new AbortController()
        let timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined
        try {
          const r = await fetch(`${baseUrl}${path}`, {
            method,
            headers,
            body: body === undefined ? undefined : isMultipart ? (body as FormData) : JSON.stringify(body),
            signal: controller.signal,
          })
          if (timer !== undefined) {
            clearTimeout(timer)
            timer = setTimeout(() => controller.abort(), timeoutMs * 4)
          }
          if (opts.responseType === 'blob' && r.ok) return (await r.blob()) as T
          text = await r.text()
          res = r
        } catch (e) {
          if (e instanceof Error && e.name === 'AbortError') throw noResponse()
          throw e
        } finally {
          if (timer !== undefined) clearTimeout(timer)
        }
      }

      let json: unknown
      try {
        json = text ? JSON.parse(text) : null
      } catch {
        json = text
      }

      if (!res.ok) {
        if (res.status === 401 || res.status === 419) onUnauthorized?.()
        const parsed = errorBody.safeParse(json)
        const serverMsg =
          parsed.success ? (parsed.data.message ?? parsed.data.error ?? detailMessage(parsed.data.detail)) : undefined
        throw new ApiError(res.status, serverMsg ?? fallbackMessage(res.status), json, serverMsg)
      }

      if (!rawEnvelope) checkEnvelopeStatus(json, onUnauthorized)

      if (schema) {
        const parsed = schema.safeParse(json)
        if (!parsed.success) {
          throw new ApiError(res.status, `Response shape mismatch for ${path}: ${parsed.error.message}`, json)
        }
        return parsed.data
      }
      return json as T
    },
  }
}
